import React, { Reducer, useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Grid, IconButton, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import reducer, {
  ClaimsTableAction,
  ClaimsTableActionType,
  ClaimsTableState,
  initialState,
} from './ClaimsTableReducer';
import ClaimsService from 'services/api/ClaimsService';
import Table from 'components/shared/Table';
import StatusLabel from 'components/shared/StatusLabel';
import TableActionButton from 'components/shared/Table/TableActionButton';
import routes from 'store/configs/Routes';
import { Claim } from 'store/types/Claim';
import { SortConfig, TableColumn } from 'store/types/Table';
import ClaimStatus from 'store/enums/ClaimStatus';
import claimStatusConfig from 'store/configs/ClaimStatusConfig';
import ClaimType from 'store/enums/ClaimType';
import ClaimsTableType from 'store/enums/ClaimsTableType';
import ClaimUploadType from 'store/enums/ClaimUploadType';
import { getHashRouteUrl } from 'util/Route';
import { defaultGridContainerProps, defaultGridItemProps, defaultSnackbarErrorProps } from 'util/Layout';
import { getFormattedDate } from 'util/Format';

interface ClaimsTableProps {
  tableType: ClaimsTableType;
  historic?: boolean;
  companyId?: string;
  data?: Claim[];
  loading?: boolean;
  onApplicationDeleteClick?: (claim: Claim) => void;
  onDisputeClaimClick?: (claim: Claim) => void;
  onFileUploadClick?: (claim: Claim, uploadType: ClaimUploadType) => void;
}

const getStatusLabel = (status: ClaimStatus): React.ReactNode => {
  if (!status) return null;

  const { theme = 'grey', name = '' } = claimStatusConfig[status];

  return <StatusLabel theme={theme} status={name} fullWidth />;
};

const getStatusRow = (record: Claim) => {
  return record.type === ClaimType.Claim ? getStatusLabel(record.claimStatus) : getStatusLabel(record.disputeStatus);
};

const ClaimsTable: React.FunctionComponent<ClaimsTableProps> = ({
  tableType,
  historic = false,
  companyId,
  data = [],
  loading,
  onApplicationDeleteClick,
  onDisputeClaimClick,
  onFileUploadClick,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [{ list, sort }, dispatch] = useReducer<Reducer<ClaimsTableState, ClaimsTableAction>>(reducer, initialState);
  const [loadingFile, setLoadingFile] = useState<boolean>(false);

  const handleSortChange = useCallback((newSort: SortConfig) => {
    dispatch({ type: ClaimsTableActionType.UpdateSort, payload: { sort: newSort } });
  }, []);

  const handleJudgeFileDownload = useCallback(
    (record: Claim) => () => {
      console.log('download');
      if (!companyId) return;

      setLoadingFile(true);
      ClaimsService.getClaimJudgeFile(record.id, companyId, record.fileNameJudgesPackage)
        .catch((error: string) => {
          enqueueSnackbar(error, defaultSnackbarErrorProps);
        })
        .finally(() => setLoadingFile(false));
    },
    [companyId, enqueueSnackbar]
  );

  const columns: Array<TableColumn<Claim>> = useMemo(() => {
    const result: Array<TableColumn<Claim>> = [
      {
        dataIndex: 'referenceNumber',
        label: <Typography sx={{ width: '180px' }}>Ref #</Typography>,
        sortable: true,
        width: '10%',
        render: (value: string) => <Typography>{value}</Typography>,
      },
      {
        dataIndex: 'type',
        label: 'Type',
        align: 'center',
        sortable: true,
        width: '7%',
        render: (type: string) => (
          <StatusLabel
            theme={type === ClaimType.Dispute ? 'secondary-theme' : 'primary-theme'}
            status={type === ClaimType.Dispute ? 'In Dispute' : 'Claim'}
          />
        ),
      },
      {
        dataIndex: 'claimStatus',
        label: 'Status',
        align: 'center',
        sortable: true,
        width: '7%',
        render: (_any, record: Claim) => getStatusRow(record),
      },
    ];

    if (tableType === ClaimsTableType.MyClaims && historic) {
      result.push(
        {
          dataIndex: 'debtorCompanyName',
          label: 'Member',
          sortable: true,
          render: (value: string) => <Typography>{value}</Typography>,
        },
        {
          dataIndex: 'toBePaidBy',
          label: 'Paid By',
          align: 'center',
          width: '10%',
          render: (value: string) => <Typography>{value}</Typography>,
        },
        {
          dataIndex: 'currencyType',
          label: <Typography textAlign={'center'}>Currency Type</Typography>,
          sortable: true,
          align: 'left',
          width: '7%',
          render: (value: string) => <Typography>{value}</Typography>,
        },
        {
          dataIndex: 'currencyTotal',
          label: <Typography textAlign={'center'}>Currency Total</Typography>,
          sortable: true,
          align: 'left',
          width: '7%',
          render: (value: string) => <Typography>{value}</Typography>,
        }
      );
    } else if (tableType === ClaimsTableType.ClaimsAgainstMe && historic) {
      result.push(
        {
          dataIndex: 'claimantCompanyName',
          label: 'Member',
          sortable: true,
          render: (value: string) => <Typography>{value}</Typography>,
        },
        {
          dataIndex: 'toBePaidBy',
          label: 'Paid By',
          align: 'center',
          width: '10%',
          render: (value: string) => <Typography>{value}</Typography>,
        },
        {
          dataIndex: 'currencyType',
          label: <Typography textAlign={'center'}>Currency Type</Typography>,
          sortable: true,
          align: 'left',
          width: '7%',
          render: (value: string) => <Typography>{value}</Typography>,
        },
        {
          dataIndex: 'currencyTotal',
          label: <Typography textAlign={'center'}>Currency Total</Typography>,
          sortable: true,
          align: 'left',
          width: '7%',
          render: (value: string) => <Typography>{value}</Typography>,
        }
      );
    } else if (tableType === ClaimsTableType.MyClaims) {
      result.push(
        {
          dataIndex: 'debtorCompanyName',
          label: <Typography textAlign={'center'}>Member</Typography>,
          sortable: true,
          width: '17%',
          render: (value: string) => <Typography>{value}</Typography>,
        },
        {
          dataIndex: 'toBePaidBy',
          label: 'To Be Paid By',
          align: 'center',
          width: '10%',
          render: (value: string) => <Typography>{value}</Typography>,
        },
        {
          dataIndex: 'currencyType',
          label: <Typography textAlign={'center'}>Currency Type</Typography>,
          sortable: true,
          align: 'left',
          width: '7%',
          render: (value: string) => <Typography>{value}</Typography>,
        },
        {
          dataIndex: 'currencyTotal',
          label: <Typography textAlign={'center'}>Currency Total</Typography>,
          sortable: true,
          align: 'left',
          width: '7%',
          render: (value: string) => <Typography>{value}</Typography>,
        },
        {
          dataIndex: 'toBePaidBy',
          label: '',
          align: 'center',
          width: '10%',
          render: () => <></>,
        },
        {
          key: 'actions',
          label: 'Action',
          align: 'center',
          width: '10%',
          render: (_: any, record: Claim) => {
            const { type, claimStatus, claimApplicationId, disputeStatus, disputeApplicationId } = record;

            if (type === ClaimType.Claim && claimStatus === ClaimStatus.Draft) {
              return (
                <TableActionButton
                  href={getHashRouteUrl(`${routes.editApplication}/${claimApplicationId}`)}
                  actionLabel={'Edit'}
                  nameLabel={'Application'}
                />
              );
            }

            if (type === ClaimType.Claim && claimStatus === ClaimStatus.Submitted) {
              return (
                <TableActionButton
                  href={getHashRouteUrl(`${routes.viewApplication}/${claimApplicationId}`)}
                  actionLabel={'View'}
                  nameLabel={'Application'}
                />
              );
            }

            if (type === ClaimType.Dispute && disputeStatus === ClaimStatus.Draft) {
              return (
                <TableActionButton
                  href={getHashRouteUrl(`${routes.viewApplication}/${disputeApplicationId}`)}
                  actionLabel={'Edit'}
                  nameLabel={'Application'}
                />
              );
            }

            if (type === ClaimType.Dispute && disputeStatus === ClaimStatus.Submitted) {
              return (
                <TableActionButton
                  href={getHashRouteUrl(`${routes.editApplication}/${disputeApplicationId}`)}
                  actionLabel={'View'}
                  nameLabel={'Application'}
                />
              );
            }

            if (type === ClaimType.Claim && claimStatus === ClaimStatus.PaymentDue) {
              return (
                <TableActionButton
                  color={'secondary'}
                  actionLabel={'Upload'}
                  nameLabel={'Files'}
                  onClick={() => (onFileUploadClick ? onFileUploadClick(record, ClaimUploadType.Files) : null)}
                />
              );
            }

            return null;
          },
        },
        {
          key: 'delete',
          label: '',
          align: 'center',
          size: 'small',
          width: '10%',
          render: (_: any, record: Claim) => {
            const { type, claimStatus, disputeStatus } = record;

            if (type === ClaimType.Claim && claimStatus === ClaimStatus.Draft) {
              return (
                <IconButton onClick={() => (onApplicationDeleteClick ? onApplicationDeleteClick(record) : null)}>
                  <DeleteIcon color="error" />
                </IconButton>
              );
            }

            if (type === ClaimType.Dispute && disputeStatus === ClaimStatus.Draft) {
              return (
                <IconButton onClick={() => (onApplicationDeleteClick ? onApplicationDeleteClick(record) : null)}>
                  <DeleteIcon color="error" />
                </IconButton>
              );
            }

            return <></>;
          },
        }
      );
    } else if (tableType === ClaimsTableType.ClaimsAgainstMe) {
      result.push(
        {
          dataIndex: 'claimantCompanyName',
          label: 'Member',
          sortable: true,
          width: '17%',
          render: (value: string) => <Typography>{value}</Typography>,
        },
        {
          dataIndex: 'toBePaidBy',
          label: 'To Be Paid By',
          align: 'center',
          width: '10%',
          render: (value: string) => <Typography>{value}</Typography>,
        },
        {
          dataIndex: 'currencyType',
          label: <Typography textAlign={'center'}>Currency Type</Typography>,
          sortable: true,
          align: 'left',
          width: '7%',
          render: (value: string) => <Typography>{value}</Typography>,
        },
        {
          dataIndex: 'currencyTotal',
          label: <Typography textAlign={'center'}>Currency Total</Typography>,
          sortable: true,
          align: 'left',
          width: '7%',
          render: (value: string) => <Typography>{value}</Typography>,
        },
        {
          key: 'proof',
          label: 'Proof Of Payment',
          align: 'center',
          width: '10%',
          render: (_: any, record: Claim) => {
            const { type, claimStatus, disputeStatus, toBePaidBy } = record;

            if (type === ClaimType.Claim && claimStatus === ClaimStatus.PaymentDue)
              return (
                <TableActionButton
                  color={'secondary'}
                  actionLabel={'Proof'}
                  nameLabel={'of Payment'}
                  onClick={() => (onFileUploadClick ? onFileUploadClick(record, ClaimUploadType.ProofOfPayment) : null)}
                />
              );

            if (
              (type === ClaimType.Dispute &&
                disputeStatus === ClaimStatus.DecisionIssued &&
                toBePaidBy?.toLowerCase() === 'debtor') ||
              toBePaidBy?.toLowerCase() === 'split'
            )
              return (
                <TableActionButton
                  color={'secondary'}
                  actionLabel={'Proof'}
                  nameLabel={'of Payment'}
                  onClick={() => (onFileUploadClick ? onFileUploadClick(record, ClaimUploadType.ProofOfPayment) : null)}
                />
              );

            return null;
          },
        },
        {
          key: 'documentation',
          label: 'Action',
          align: 'center',
          width: '10%',
          render: (_: any, record: Claim) => {
            const { type, claimStatus, disputeStatus } = record;

            if (type === ClaimType.Claim && claimStatus === ClaimStatus.PaymentDue)
              return (
                <TableActionButton
                  color={'secondary'}
                  actionLabel={'Upload'}
                  nameLabel={'Files'}
                  onClick={() => (onFileUploadClick ? onFileUploadClick(record, ClaimUploadType.Files) : null)}
                />
              );
            else if (type === ClaimType.Dispute && disputeStatus === ClaimStatus.InQueue)
              return (
                <TableActionButton
                  color={'secondary'}
                  actionLabel={'Upload'}
                  nameLabel={'Files'}
                  onClick={() => (onFileUploadClick ? onFileUploadClick(record, ClaimUploadType.Files) : null)}
                />
              );

            return null;
          },
        },
        {
          key: 'actions',
          label: 'Dispute',
          align: 'center',
          width: '10%',
          render: (_: any, record: Claim) => {
            const { type, claimStatus } = record;

            if (type === ClaimType.Claim && claimStatus === ClaimStatus.PaymentDue)
              return (
                <TableActionButton
                  actionLabel={'Dispute'}
                  onClick={() => (onDisputeClaimClick ? onDisputeClaimClick(record) : null)}
                />
              );

            return null;
          },
        }
      );
    } else if (tableType === ClaimsTableType.UnderReviewClaims) {
      result.push(
        {
          dataIndex: 'dateAssigned',
          label: 'Date Assigned',
          sortable: true,
          render: (value: string) => <Typography>{getFormattedDate(value)}</Typography>,
        },
        {
          dataIndex: 'claimantCompanyName',
          label: 'Filed By',
          sortable: true,
          render: (value: string) => <Typography>{value}</Typography>,
        },
        {
          dataIndex: 'debtorCompanyName',
          label: 'Filed Against',
          sortable: true,
          render: (value: string) => <Typography>{value}</Typography>,
        },
        {
          key: 'documentation',
          label: 'Documentation',
          align: 'center',
          render: (_: any, record: Claim) => {
            return (
              <TableActionButton
                actionLabel={'Download'}
                nameLabel={'Files'}
                disabled={loadingFile}
                onClick={handleJudgeFileDownload(record)}
              />
            );
          },
        }
      );
    }

    return result;
  }, [
    handleJudgeFileDownload,
    historic,
    loadingFile,
    onApplicationDeleteClick,
    onDisputeClaimClick,
    onFileUploadClick,
    tableType,
  ]);

  useEffect(() => {
    if (data) {
      dispatch({
        type: ClaimsTableActionType.SetInitialList,
        payload: { initialList: data },
      });
    }
  }, [data]);

  return (
    <Grid {...defaultGridContainerProps}>
      <Grid {...defaultGridItemProps}>
        <Table columns={columns} list={list} sort={sort} onSortChange={handleSortChange} loading={loading} />
      </Grid>
    </Grid>
  );
};

export default ClaimsTable;
